import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, LinkButton, Notice, TableBox, TextField } from './ui.js';

type Fix = Schemas['FixContent'];

function CopyButton({
  value,
  label,
  t: fixedT,
  variant = 'soft',
}: {
  value: string;
  /** Which value: several "Kopieren" on one page are told apart by it. */
  label?: string;
  t?: TFunction;
  /** The fix page's buttons are secondary (fix.html); the panel's stay soft. */
  variant?: 'soft' | 'secondary';
}) {
  const { t: own } = useTranslation();
  const t = fixedT ?? own;
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant={variant}
      aria-label={label ? t('fixes.copyLabel', { label }) : undefined}
      size="sm"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
    >
      {copied ? t('fixes.copied') : t('fixes.copy')}
    </Button>
  );
}

/**
 * A ready-made fix sent to a web designer (T-72; fix.html .fixitem): what, where / who / effort,
 * the steps, the values to copy, page by page, warnings. `t` is the owner's language.
 */
export function FixContentView({ fix, t: fixedT }: { fix: Fix; t?: TFunction }) {
  const { t: own } = useTranslation();
  const t = fixedT ?? own;
  return (
    <>
      <p className="bodyM">{fix.summary}</p>
      <div className="kv">
        <div>
          <span>{t('fixes.where')}</span>
          <b>{fix.where}</b>
        </div>
        <div>
          <span>{t('fixes.who')}</span>
          <b>{t(`fixes.needs.${fix.needs}`)}</b>
        </div>
        <div>
          <span>{t('fixes.time')}</span>
          <b>{t('fixes.minutes', { count: fix.minutes })}</b>
        </div>
      </div>
      {fix.steps.length > 0 ? (
        <>
          <b className="h4s">{t('fixes.steps')}</b>
          <ol>
            {fix.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </>
      ) : null}
      {fix.values.map((v, i) => (
        <div key={i} className="code">
          <div>
            <span className="label">{v.label}</span>
            {v.kind === 'text' ? (
              <p>{v.value}</p>
            ) : (
              <pre tabIndex={0}>
                <code>{v.value}</code>
              </pre>
            )}
          </div>
          <CopyButton value={v.value} label={v.label} t={t} variant="secondary" />
        </div>
      ))}
      {fix.table ? (
        <TableBox label={t('fixes.table')}>
          <table>
            <thead>
              <tr>
                {fix.table.columns.map((c, i) => (
                  <th key={i} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fix.table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      ) : null}
      {fix.warnings.length > 0 ? (
        <Notice kind="warning">
          <ul className="warnings">
            {fix.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </>
  );
}

/**
 * The fix inside the problem (site-findings.html .fixpanel): what it is, where / who / how long in
 * one line, the steps, the values to copy, page by page, warnings.
 */
function PanelContent({ fix }: { fix: Fix }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="bodyM">{fix.summary}</p>
      <div className="meta">
        <span>{t('fixes.whereIs', { where: fix.where })}</span>{' '}
        <span>{t('fixes.whoIs', { who: t(`fixes.needs.${fix.needs}`) })}</span>{' '}
        <span>{t('fixes.timeIs', { time: t('fixes.minutes', { count: fix.minutes }) })}</span>
      </div>
      {fix.steps.length > 0 ? (
        <div>
          <b className="h4s">{t('fixes.steps')}</b>
          <ol>
            {fix.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}
      {fix.values.map((v, i) => (
        <div key={i} className="value">
          <div className="vhd">
            <b>{v.label}</b>
            <CopyButton value={v.value} />
          </div>
          {v.kind === 'text' ? (
            <p className="text">{v.value}</p>
          ) : (
            // Scrolls sideways when long, so it takes the keyboard focus (axe).
            <pre tabIndex={0}>
              <code>{v.value}</code>
            </pre>
          )}
        </div>
      ))}
      {fix.table ? (
        // Scrolls sideways on a phone: focusable as a region that scrolls must be.
        <div className="table" tabIndex={0} role="region" aria-label={t('fixes.table')}>
          <table>
            <thead>
              <tr>
                {fix.table.columns.map((c, i) => (
                  <th key={i}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fix.table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {fix.warnings.length > 0 ? (
        <Notice kind="warning">
          {fix.warnings.length === 1 ? (
            fix.warnings[0]
          ) : (
            <ul>
              {fix.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </Notice>
      ) : null}
    </>
  );
}

function ShareForm({ siteId, ruleCode }: { siteId: string; ruleCode: string }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const messageId = useId();
  const share = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/fix-shares', {
          params: { path: { siteId } },
          body: {
            ruleCodes: [ruleCode],
            email: email.trim(),
            ...(message.trim() ? { message: message.trim() } : {}),
          },
        }),
      ),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    share.mutate();
  };
  if (share.isSuccess)
    return <Notice kind="success">{t('fixes.shareSent', { email: share.data.email })}</Notice>;
  return (
    <form onSubmit={submit} className="share">
      <p className="hint">{t('fixes.shareHint')}</p>
      <TextField
        label={t('fixes.shareEmail')}
        type="email"
        required
        maxLength={254}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div className="field">
        <label htmlFor={messageId}>
          {t('fixes.shareMessage')} ({t('app.optional')})
        </label>
        <textarea
          id={messageId}
          value={message}
          maxLength={1000}
          rows={3}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      {share.isError ? <Notice kind="error">{errorMessage(share.error, t)}</Notice> : null}
      <div>
        <Button type="submit" size="sm" busy={share.isPending}>
          {t('fixes.shareSend')}
        </Button>
      </div>
    </form>
  );
}

/** "Fertige Lösung" of a problem (T-72, site-findings.html): make it, copy, check, share. */
export function FixPanel({ siteId, ruleCode }: { siteId: string; ruleCode: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const titleId = useId();
  const key = ['site', siteId, 'fix', ruleCode];
  const [sharing, setSharing] = useState(false);
  const state = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/fixes/{ruleCode}', { params: { path: { siteId, ruleCode } } }),
      ),
  });
  const make = useMutation({
    mutationFn: (regenerate: boolean) =>
      unwrap(
        api.POST('/sites/{siteId}/fixes/{ruleCode}', {
          params: { path: { siteId, ruleCode } },
          body: { regenerate },
        }),
      ),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  const recheck = useMutation({
    mutationFn: () => unwrap(api.POST('/sites/{siteId}/recheck', { params: { path: { siteId } } })),
  });
  const s = state.data;
  if (!s || s.ownerOnly || s.openProblems === 0 || (!s.available && !s.locked)) return null;

  const head = (quota: boolean) => (
    <div className="hd">
      <b id={titleId}>{t('fixes.title')}</b>
      {quota ? (
        <span className="spec">
          {t('fixes.quotaShort', { used: s.quota.used, limit: s.quota.limit })}
        </span>
      ) : null}
    </div>
  );

  if (s.locked)
    return (
      <section className="fixpanel" aria-labelledby={titleId}>
        {head(false)}
        <div className="upsell">
          <span>{t('fixes.locked')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('fixes.upgrade')}
          </LinkButton>
        </div>
      </section>
    );

  const pkg = s.package;
  const busy = make.isPending;
  return (
    <section className="fixpanel" aria-labelledby={titleId}>
      {head(Boolean(pkg) && s.kind === 'ai')}
      {!pkg ? (
        <>
          <p className="bodyM">
            {s.kind === 'ai'
              ? t('fixes.introAi', { used: s.quota.used, limit: s.quota.limit })
              : t('fixes.introTemplate')}
          </p>
          <div className="acts">
            <Button size="sm" busy={busy} onClick={() => make.mutate(false)}>
              {t('fixes.create')}
            </Button>
          </div>
          {busy && s.kind === 'ai' ? <p className="hint">{t('fixes.working')}</p> : null}
        </>
      ) : (
        <>
          {s.stale ? (
            <Notice
              kind="info"
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  busy={busy}
                  onClick={() => make.mutate(false)}
                >
                  {t('fixes.update')}
                </Button>
              }
            >
              {t('fixes.stale')}
            </Notice>
          ) : null}
          <PanelContent fix={pkg.content} />
          <div className="acts">
            <Button size="sm" busy={recheck.isPending} onClick={() => recheck.mutate()}>
              {t('fixes.recheck')}
            </Button>
            {s.regenerationsLeft > 0 && !s.stale ? (
              <Button variant="secondary" size="sm" busy={busy} onClick={() => make.mutate(true)}>
                {t('fixes.regenerate', { count: s.regenerationsLeft })}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              aria-expanded={sharing}
              onClick={() => setSharing(!sharing)}
            >
              {t('fixes.share')}
            </Button>
          </div>
          {recheck.isSuccess ? <Notice kind="success">{t('fixes.rechecking')}</Notice> : null}
          {recheck.isError ? <Notice kind="error">{errorMessage(recheck.error, t)}</Notice> : null}
          {sharing ? <ShareForm siteId={siteId} ruleCode={ruleCode} /> : null}
        </>
      )}
      {make.isError ? <Notice kind="error">{errorMessage(make.error, t)}</Notice> : null}
    </section>
  );
}
